/**
 * Real `@itk-wasm/compare-images` (official, pre-built WASM, v5.4.2). Lazy
 * pipeline fetch, see `downsampleClient.ts`'s header for the shared notes.
 *
 * NOTE: the shipped `.d.ts` names the per-pixel count field
 * `numberOfPixelsDifferent`, but the real runtime JSON result uses
 * `numberOfPixelsWithDifferences` -- confirmed by running the real Node
 * pipeline against synthetic data (`scripts/verify-itk-wasm-tools.mjs`), not
 * assumed from the (stale) type declaration. `CompareMetrics` below reflects
 * the real shape.
 */
import { compareImages } from "@itk-wasm/compare-images";

import type { NiftiVolume } from "@/lib/nifti";
import { itkImageToNiftiVolume, niftiVolumeToItkImage } from "./itkImage";

export interface CompareMetrics {
  almostEqual: boolean;
  numberOfPixelsWithDifferences: number;
  minimumDifference: number;
  maximumDifference: number;
  meanDifference: number;
  totalDifference: number;
}

export interface CompareResult {
  metrics: CompareMetrics;
  differenceImage: NiftiVolume;
}

export async function compareVolumes(test: NiftiVolume, baseline: NiftiVolume, differenceThreshold = 0): Promise<CompareResult> {
  const testImage = niftiVolumeToItkImage(test, "test");
  const baselineImage = niftiVolumeToItkImage(baseline, "baseline");
  const { metrics, differenceImage } = await compareImages(testImage, {
    baselineImages: [baselineImage],
    differenceThreshold,
  });
  return {
    metrics: metrics as unknown as CompareMetrics,
    differenceImage: itkImageToNiftiVolume(differenceImage),
  };
}
